import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navItems } from '../mock';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';
import { BTX_REFERRAL_URL } from '../config/links';

const BTX_MARKETPLACE_URL = BTX_REFERRAL_URL;
const LOGIN_URL = 'https://azreservesaccountportal.zite.so';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileDropdownOpen(null);
  };

  const handleNavigation = (e, href) => {
    e.preventDefault();
    if (href.startsWith('/#')) {
      const hash = href.substring(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.getElementById(hash);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    closeMenus();
  };

  const handleContact = (e) => {
    e.preventDefault();
    closeMenus();
    openFilloutPopup();
  };

  const toggleMobileDropdown = (label) => {
    setMobileDropdownOpen(mobileDropdownOpen === label ? null : label);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg' : ''}`}
      style={{ backgroundColor: colors.black }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center z-50"
          data-testid="nav-logo-link"
        >
          <img src="/logo-white-header.png" alt="Arizore" className="h-12 sm:h-16 w-auto" />
        </a>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-5 lg:space-x-7">
          {navItems.map((item) => (
            item.dropdown ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-trigger`}
                  className="text-sm transition-colors duration-200 flex items-center space-x-1 py-2"
                  style={{ color: colors.white, opacity: 0.8 }}
                >
                  <span>{item.label}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === item.label && (
                  <div className="absolute top-full left-0 pt-2 rounded-lg shadow-lg min-w-[220px]" style={{ backgroundColor: colors.graphite }}>
                    <div className="py-2">
                      {item.dropdown.map((dropItem) => (
                        dropItem.comingSoon ? (
                          <div
                            key={dropItem.label}
                            data-testid={`nav-link-${dropItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="block px-4 py-2 text-sm cursor-not-allowed"
                            style={{ color: colors.white, opacity: 0.4 }}
                          >
                            <span>{dropItem.label}</span>
                            <span className="text-xs ml-2" style={{ color: colors.teal, opacity: 0.8 }}>(Coming soon)</span>
                          </div>
                        ) : (
                          <a
                            key={dropItem.label}
                            href={dropItem.href}
                            onClick={(e) => handleNavigation(e, dropItem.href)}
                            data-testid={`nav-link-${dropItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="block px-4 py-2 text-sm transition-colors duration-200"
                            style={{ color: colors.white, opacity: 0.8 }}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = `${colors.teal}20`; e.target.style.opacity = 1; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.opacity = 0.8; }}
                          >
                            {dropItem.label}
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.label}
                href={item.isContact ? '#' : item.href}
                onClick={(e) => item.isContact ? handleContact(e) : handleNavigation(e, item.href)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                className="text-sm transition-colors duration-200"
                style={{ color: colors.white, opacity: 0.8 }}
                onMouseEnter={(e) => { e.target.style.color = colors.teal; e.target.style.opacity = 1; }}
                onMouseLeave={(e) => { e.target.style.color = colors.white; e.target.style.opacity = 0.8; }}
              >
                {item.label}
              </a>
            )
          ))}

          {/* Login - account portal */}
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login-btn"
            className="px-4 py-2 text-sm rounded-lg transition-all duration-300 inline-block"
            style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}`; e.currentTarget.style.color = colors.teal; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = colors.white; }}
          >
            Login
          </a>

          {/* PRIMARY CTA - Continue to BTX Marketplace (referral URL preserved) */}
          <a
            href={BTX_MARKETPLACE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-btx-marketplace-cta"
            className="px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-300 inline-block"
            style={{ backgroundColor: colors.teal, color: colors.white, textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}33`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Continue to BTX Marketplace
          </a>
        </div>

        {/* Mobile Menu Button */}
        {!mobileMenuOpen && (
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(true)}
            style={{ color: colors.white, zIndex: 10000, position: 'relative' }}
            aria-label="Open menu"
            data-testid="nav-mobile-open-btn"
          >
            <Menu size={24} />
          </button>
        )}
      </div>

      {/* Mobile Menu Side Drawer */}
      <>
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            style={{ zIndex: 45 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <div
          className={`md:hidden fixed top-0 left-0 h-full w-80 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
          style={{ backgroundColor: colors.black, zIndex: 50 }}
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <img src="/arizore-logo-white-mobile.png" alt="Arizore" className="h-5 w-auto" />
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 hover:opacity-80 transition-opacity"
              style={{ color: colors.white }}
              aria-label="Close menu"
              data-testid="nav-mobile-close-btn"
            >
              <X size={28} />
            </button>
          </div>

          {/* Menu Content */}
          <div className="overflow-y-auto px-6 py-8" style={{ height: 'calc(100vh - 80px)' }}>
            <div className="space-y-6">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.label} className="space-y-3">
                    <button
                      onClick={() => toggleMobileDropdown(item.label)}
                      data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}-trigger`}
                      className="flex items-center justify-between w-full text-xl font-semibold text-left"
                      style={{ color: colors.white }}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-300 ${mobileDropdownOpen === item.label ? 'rotate-180' : ''}`}
                        style={{ color: colors.white, opacity: 0.6 }}
                      />
                    </button>
                    <div className={`pl-4 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${mobileDropdownOpen === item.label ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {item.dropdown.map((dropItem) => (
                        dropItem.comingSoon ? (
                          <div
                            key={dropItem.label}
                            data-testid={`nav-mobile-link-${dropItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="block text-base"
                            style={{ color: colors.white, opacity: 0.4 }}
                          >
                            {dropItem.label}
                            <span className="text-xs ml-2" style={{ color: colors.teal }}>(Coming soon)</span>
                          </div>
                        ) : (
                          <a
                            key={dropItem.label}
                            href={dropItem.href}
                            onClick={(e) => handleNavigation(e, dropItem.href)}
                            data-testid={`nav-mobile-link-${dropItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="block text-base transition-colors duration-200"
                            style={{ color: colors.teal }}
                          >
                            {dropItem.label}
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                ) : (
                  <a
                    key={item.label}
                    href={item.isContact ? '#' : item.href}
                    onClick={(e) => item.isContact ? handleContact(e) : handleNavigation(e, item.href)}
                    data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                    className="block text-xl font-semibold hover:opacity-80 transition-opacity"
                    style={{ color: colors.white }}
                  >
                    {item.label}
                  </a>
                )
              ))}

              {/* Login + BTX Marketplace CTAs */}
              <div className="pt-4 border-t space-y-3" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <a
                  href={LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-login-btn"
                  className="block w-full text-center px-4 py-3 text-base font-semibold rounded-lg transition-all duration-300"
                  style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
                >
                  Login
                </a>
                <a
                  href={BTX_MARKETPLACE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-btx-marketplace-cta"
                  className="block w-full text-center px-4 py-3 text-base font-semibold rounded-lg transition-all duration-300"
                  style={{ backgroundColor: colors.teal, color: colors.white }}
                >
                  Continue to BTX Marketplace
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    </nav>
  );
};

export default Navigation;
